import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface DebugLog {
  id: string;
  timestamp: Date;
  type: 'info' | 'success' | 'error' | 'data';
  message: string;
  data?: any;
}
interface DebugPanelProps {
  logs: DebugLog[];
  onClear: () => void;
  isMinimized: boolean;
  onToggleMinimize: () => void;
}
export function DebugPanel({
  logs,
  onClear,
  isMinimized,
  onToggleMinimize
}: DebugPanelProps) {
  const logsEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMinimized) {
      logsEndRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [logs, isMinimized]);
  if (logs.length === 0) return null;
  const getTypeColor = (type: DebugLog['type']) => {
    switch (type) {
      case 'success':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'data':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };
  const getTypeLabel = (type: DebugLog['type']) => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✗';
      case 'data':
        return '📊';
      default:
        return 'ℹ';
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: -20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="fixed bottom-4 left-4 w-96 bg-white rounded-lg shadow-2xl border-2 border-gray-200 z-50" data-id="element-241">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50" data-id="element-242">
        <div className="flex items-center gap-2" data-id="element-243">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" data-id="element-244" />
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-245">
            OpenAI Debug Log
          </h3>
          <span className="text-xs text-gray-500" data-id="element-246">({logs.length})</span>
        </div>
        <div className="flex items-center gap-1" data-id="element-247">
          <button onClick={onToggleMinimize} className="p-1 hover:bg-gray-200 rounded transition-colors" data-id="element-248">
            {isMinimized ? <ChevronUpIcon className="w-4 h-4 text-gray-600" data-id="element-249" /> : <ChevronDownIcon className="w-4 h-4 text-gray-600" data-id="element-250" />}
          </button>
          <button onClick={onClear} className="p-1 hover:bg-gray-200 rounded transition-colors" data-id="element-251">
            <XIcon className="w-4 h-4 text-gray-600" data-id="element-252" />
          </button>
        </div>
      </div>

      {/* Logs */}
      <AnimatePresence data-id="element-253">
        {!isMinimized && <motion.div initial={{
        height: 0
      }} animate={{
        height: 'auto'
      }} exit={{
        height: 0
      }} className="overflow-hidden" data-id="element-254">
            <div className="max-h-96 overflow-y-auto p-3 space-y-2" data-id="element-255">
              {logs.map(log => <motion.div key={log.id} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} className={`p-2 rounded border text-xs ${getTypeColor(log.type)}`} data-id="element-256">
                  <div className="flex items-start gap-2" data-id="element-257">
                    <span className="font-bold" data-id="element-258">{getTypeLabel(log.type)}</span>
                    <div className="flex-1 min-w-0" data-id="element-259">
                      <div className="flex items-center justify-between gap-2 mb-1" data-id="element-260">
                        <span className="font-medium" data-id="element-261">{log.message}</span>
                        <span className="text-xs opacity-60 whitespace-nowrap" data-id="element-262">
                          {log.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      {log.data && <pre className="mt-1 p-2 bg-white/50 rounded text-xs overflow-x-auto" data-id="element-263">
                          {JSON.stringify(log.data, null, 2)}
                        </pre>}
                    </div>
                  </div>
                </motion.div>)}
              <div ref={logsEndRef} data-id="element-264" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}